import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Badge,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { Tree, TreeNode } from 'react-organizational-chart';
import styled from 'styled-components';
import { api } from '../services/api';

interface SeriesType {
  type: string;
  series: Series[];
}

interface Series {
  id: number;
  name: string;
  type: string;
  subjects: Subject[];
}

interface Subject {
  id: number;
  name: string;
  seriesId: number;
  classId: number;
  class?: Class;
}

interface Class {
  id: number;
  name: string;
}

const StyledNode = styled.div`
  padding: 5px;
  border-radius: 8px;
  display: inline-block;
  border: 1px solid #ddd;
`;

const NodeCard = styled(Card)<{ $type: 'root' | 'series' | 'subject' | 'class' }>`
  min-width: 200px;
  cursor: pointer;
  transition: all 0.2s;
  transform-origin: center;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  ${props => {
    switch (props.$type) {
      case 'root':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        `;
      case 'series':
        return `
          background: linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%);
          color: white;
        `;
      case 'subject':
        return `
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          color: white;
        `;
      case 'class':
        return `
          background: linear-gradient(135deg, #f46b45 0%, #eea849 100%);
          color: white;
        `;
      default:
        return '';
    }
  }}
`;

const StyledTreeNode = styled(TreeNode)`
  padding: 20px 0;
  
  .rst__lineHalfHorizontalRight::before,
  .rst__lineFullVertical::after,
  .rst__lineHalfVerticalTop::after,
  .rst__lineHalfVerticalBottom::after {
    background-color: #a8b0c1;
  }
`;

const HierarchyPage = () => {
  const [loading, setLoading] = useState(true);
  const [hierarchyData, setHierarchyData] = useState<SeriesType[]>([]);
  const bgColor = useColorModeValue('white', 'gray.800');

  const loadHierarchyData = async () => {
    try {
      setLoading(true);
      const [seriesData, subjects, classes] = await Promise.all([
        api.getSeries(),
        api.getSubjects(),
        api.getClasses(),
      ]);

      // Initialize series with empty subjects array
      const series = seriesData.map((s: Series) => ({
        ...s,
        subjects: []
      }));

      // Group series by type
      const seriesByType = series.reduce((acc: { [key: string]: Series[] }, curr: Series) => {
        const type = curr.type || 'Uncategorized';
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(curr);
        return acc;
      }, {});

      // Add subjects to series and associate classes
      series.forEach((s: Series) => {
        const seriesSubjects: Subject[] = subjects.filter((sub: Subject) => sub.seriesId === s.id);
        // Add class information to each subject
        const subjectsWithClasses: Subject[] = seriesSubjects.map((subject: Subject): Subject => ({
          ...subject,
          class: classes.find((cls: Class) => cls.id === subject.classId)
        }));
        s.subjects = subjectsWithClasses;
      });

      // Convert to array format
      const hierarchyData = Object.entries(seriesByType).map(([type, series]) => ({
        type,
        series: series as Series[],
      }));

      setHierarchyData(hierarchyData);
    } catch (error) {
      console.error('Error loading hierarchy data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHierarchyData();
  }, []); // Since this is only loading initial data, empty dependency array is fine

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (hierarchyData.length === 0) {
    return (
      <Alert status="info">
        <AlertIcon />
        No data available in the hierarchy.
      </Alert>
    );
  }

  const renderNode = (label: string, count: number, type: 'root' | 'series' | 'subject' | 'class') => (
    <StyledNode>
      <NodeCard $type={type}>
        <CardBody p={3}>
          <VStack spacing={2} align="center">
            <Text fontWeight="bold" fontSize={type === 'root' ? 'lg' : 'md'}>
              {label}
            </Text>
            {count > 0 && (
              <Badge 
                colorScheme={
                  type === 'root' ? 'purple' :
                  type === 'series' ? 'blue' :
                  type === 'subject' ? 'green' : 'orange'
                }
                fontSize="xs"
              >
                {count} {type === 'root' ? 'series' :
                        type === 'series' ? 'subjects' :
                        type === 'subject' ? 'class' : 'items'}
              </Badge>
            )}
          </VStack>
        </CardBody>
      </NodeCard>
    </StyledNode>
  );

  return (
    <Box p={6} overflowX="auto" minH="calc(100vh - 100px)">
      <Heading size="xl" mb={8}>📊 Content Hierarchy</Heading>
      
      <Box 
        bg={bgColor}
        p={8} 
        borderRadius="xl" 
        shadow="lg"
        minW="fit-content"
      >
        <Tree
          lineWidth="2px"
          lineColor="#a8b0c1"
          lineBorderRadius="10px"
          label={
            <StyledNode>
              <NodeCard $type="root">
                <CardBody p={4}>
                  <VStack spacing={2} align="center">
                    <Text fontSize="xl" fontWeight="bold">
                      📚 Puneet Publication
                    </Text>
                    <Badge colorScheme="purple">
                      {hierarchyData.length} Series Types
                    </Badge>
                  </VStack>
                </CardBody>
              </NodeCard>
            </StyledNode>
          }
        >
          {hierarchyData.map((seriesType) => (
            <StyledTreeNode
              key={seriesType.type}
              label={renderNode(seriesType.type, seriesType.series.length, 'root')}
            >
              {seriesType.series.map((series) => (
                <StyledTreeNode
                  key={series.id}
                  label={renderNode(series.name, series.subjects?.length || 0, 'series')}
                >
                  {series.subjects?.map((subject) => (
                    <StyledTreeNode
                      key={subject.id}
                      label={renderNode(subject.name, 1, 'subject')}
                    >
                      <StyledTreeNode
                        key={subject.class?.id}
                        label={renderNode(subject.class?.name || 'Unknown Class', 0, 'class')}
                      />
                    </StyledTreeNode>
                  ))}
                </StyledTreeNode>
              ))}
            </StyledTreeNode>
          ))}
        </Tree>
      </Box>
    </Box>
  );
};

export default HierarchyPage; 